import { ajvResolver } from '@hookform/resolvers/ajv'
import { EmailOutlined } from '@mui/icons-material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, IconButton, Link, TextField } from '@mui/material'
import { AxiosError } from 'axios'
import ImageBanner from 'components/image-banner'
import LoadingBackDrop from 'components/loading-backdrop'
import LogoTheme from 'components/logo-theme'
import PasswordValidation from 'components/password-validation'
import LoginGirlSVG from 'components/svg/login-girl'
import WrapperPermission from 'components/wrapper-permission'
import { useAlert } from 'contexts/alert'
import { useAuth } from 'contexts/auth'
import { useCustomTheme } from 'contexts/theme'
import { useCallback, useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import SignUpExternalSchema, { ISignUpExternalFormData } from 'schema/authentication/sign-up-external'
import api from 'services/api'
import { useStyles } from './styles'

interface IRouteParams {
  code: string
}

// const validationSchema = yup.object().shape({
//   cnpj: yup
//     .string()
//     .required('CNPJ é obrigatório')
//     .test('customValidation', 'CNPJ Inválido', (value) => isValidCNPJ(value)),
//   name: yup.string().required('Nome é obrigatório'),
//   email: yup.string().email('E-Mail Inválido').required('Email é obrigatório'),
//   password: yup
//     .string()
//     .required('Senha obrigatória')
//     .min(8, 'Senha deve ter no mínimo 8 caracteres.')
//     .matches(/[A-Z]/, 'Senha deve conter pelo menos uma letra maiúscula.')
//     .matches(/[a-z]/, 'Senha deve conter pelo menos uma letra minúscula.')
//     .matches(/\d/, 'Senha deve conter pelo menos um número.')
//     .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Senha deve conter pelo menos um caractere especial.'),
// })

const SignUpExternal = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isInInitialLoading, setIsInInitialLoading] = useState(true)
  const [disabled, setDisabled] = useState(true)
  const [isPasswordHidden, setIsPasswordHidden] = useState(true)
  const [isHidePasswordRequirements, setIsHidePasswordRequirements] = useState(false)

  const classes = useStyles()
  const history = useHistory()
  const { theme } = useCustomTheme()
  const params = useParams<IRouteParams>()
  const { setIsExternalAuth, signInUpdate, signInWithoutStorage } = useAuth()
  const { openAlert, closeAlert } = useAlert()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<ISignUpExternalFormData>({
    resolver: ajvResolver(SignUpExternalSchema),
  })

  const handleChange = (formField: any) => closeAlert()

  const onSubmit: SubmitHandler<ISignUpExternalFormData> = useCallback(
    async (payLoad) => {
      try {
        setIsLoading(true)

        const { data } = await api.patch(`/users/authentication-external/${params.code}`, {
          cnpj: payLoad.cnpj,
          name: payLoad.name,
          password: payLoad.password,
        })

        await signInWithoutStorage({
          userId: data.userId,
          email: payLoad.email,
          password: payLoad.password,
        })
        setIsLoading(false)
        history.push('/verify-email')
      } catch (err) {
        setIsLoading(false)

        const error = err as AxiosError
        openAlert((error.response?.data as any).message, 'error')
      }
    },
    [history]
  )

  const validateIfUserAlreadyExists = async () => {
    try {
      //code deve vir com sellerUserId:email em base 64
      const [_, email] = atob(params.code).split(':')
      setValue('email', email)

      const { data } = await api.patch(`/users/authentication-external/${params.code}`)

      signInUpdate(data.token, data.user)
      setIsInInitialLoading(false)
      history.push('/verify-email')
    } catch (error) {
      setIsInInitialLoading(false)
      console.log(error)
    }
  }

  useEffect(() => {
    if (params) {
      validateIfUserAlreadyExists()
      setIsExternalAuth(true)
    }
  }, [params])

  return (
    <Box className={classes.container}>
      <Box className={classes.mainWrapper}>
        <WrapperPermission>
          <LoadingBackDrop isloading={isInInitialLoading} />
          <Box
            component='form'
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            data-testid='form'
            className={classes.formBox}
          >
            <LogoTheme />

            <span className={classes.formTitle}>
              Faça login em sua conta empresarial para autenticar e proteger seus dados privados com segurança.
            </span>

            <TextField
              required
              id='cnpj'
              label='CNPJ'
              autoFocus
              variant='outlined'
              placeholder='Digite o CNPJ da sua Empresa'
              helperText={errors?.cnpj?.message}
              error={Boolean(errors.cnpj)}
              disabled={isLoading}
              sx={{ mb: 3 }}
              {...register('cnpj', { onChange: (e) => handleChange(e) })}
            />

            <TextField
              required
              id='name'
              label='Razão Social'
              variant='outlined'
              placeholder='Digite a Razão Social da sua Empresa'
              error={Boolean(errors.name)}
              helperText={errors?.name?.message}
              disabled={isLoading}
              sx={{ mb: 3 }}
              {...register('name', { onChange: (e) => handleChange(e) })}
            />

            <TextField
              required
              id='email'
              label='E-mail'
              type='email'
              variant='outlined'
              placeholder='Digite seu e-mail'
              error={Boolean(errors.email)}
              helperText={errors?.email?.message}
              disabled={true}
              sx={{ mb: 3 }}
              {...register('email', { onChange: (e) => handleChange(e) })}
              InputProps={{
                startAdornment: (
                  <EmailOutlined
                    color={isLoading ? 'disabled' : 'inherit'}
                    sx={{ mr: 1.5 }}
                  />
                ),
              }}
            />

            <Box
              position='relative'
              width='100%'
            >
              <TextField
                required
                id='password'
                label='Senha'
                type={isPasswordHidden ? 'password' : 'text'}
                autoComplete='new-password'
                variant='outlined'
                placeholder='Escolha uma senha'
                error={Boolean(errors.password)}
                helperText={errors?.password?.message}
                onFocus={() => setIsHidePasswordRequirements(true)}
                onBlurCapture={() => setIsHidePasswordRequirements(false)}
                disabled={isLoading}
                {...register('password', { onChange: (e) => handleChange(e) })}
                InputProps={{
                  startAdornment: (
                    <LockOutlinedIcon
                      color={isLoading ? 'disabled' : 'inherit'}
                      sx={{ mr: 1.5 }}
                    />
                  ),
                  endAdornment: (
                    <IconButton
                      onClick={() => setIsPasswordHidden(!isPasswordHidden)}
                      disabled={isLoading}
                    >
                      {isPasswordHidden ? (
                        <VisibilityOffOutlinedIcon color={isLoading ? 'disabled' : 'inherit'} />
                      ) : (
                        <VisibilityOutlinedIcon />
                      )}
                    </IconButton>
                  ),
                  style: { paddingRight: 1 },
                }}
              />
              {isHidePasswordRequirements && <PasswordValidation passwordString={watch('password')} />}
            </Box>

            <div className={classes.acceptArea}>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(e) => setDisabled(!e.target.checked)}
                    disabled={isLoading}
                  />
                }
                label={
                  <div>
                    <span>Eu li e concordo </span>
                    <Link
                      download
                      href='/documents/Termose_Condicoes_Conducco-10_01_2024.pdf'
                    >
                      Termos de Uso
                    </Link>
                    <span> e a </span>
                    <Link
                      download
                      href='/documents/Politica_Privacidade.pdf'
                    >
                      Política de Privacidade
                    </Link>
                    <span> da Conducco</span>
                  </div>
                }
              />
            </div>

            <Button
              type='submit'
              fullWidth
              variant='contained'
              size='large'
              disabled={isLoading || disabled}
              sx={{
                mt: 2,
                mb: 1,
                '&.Mui-disabled': {
                  backgroundColor: '#8C9097 !important',
                  color: '#FFF !important',
                },
              }}
            >
              {isLoading ? (
                <CircularProgress
                  color='inherit'
                  size={26}
                />
              ) : (
                'CRIAR UMA CONTA'
              )}
            </Button>
          </Box>
        </WrapperPermission>

        <ImageBanner
          title='Conducco uma maneira eficiente e segura de gerenciar seus dados corporativos'
          banner={<LoginGirlSVG />}
        />
      </Box>
    </Box>
  )
}

export default SignUpExternal
